import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useAuth } from '@/auth/AuthProvider';
import { firestore } from '@/lib/firebase';
import { trackEvents } from '@/lib/events';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { serverNow, assertCanWrite } from '@etiquetachef/ui-shared/online';

const D = 24 * 60 * 60 * 1000;

export type AcknowledgeButtonProps = {
  alertId: string;
  alertKind: string;
  observedAtMs: number;
  acknowledgedAt: number | null;
};

/**
 * Botão "marcar como visto" de um alerta de BI; já reconhecido, vira texto.
 *
 * Origem: sem issue — commit d4e0990a9, 2026-05-04 (ack mutation do
 * dashboard de alertas, Sprint 4.13).
 *
 * Contrato: props do alerta. Clique (só com `AUTHORIZED`) faz `updateDoc`
 * em `aggregations/alerts/items/{alertId}` gravando SÓ `acknowledgedBy` (uid
 * de quem clicou) e emite `trackEvents.biAlertAcknowledged(kind, idadeEmDias)`.
 * Falha → mensagem inline + `reportNonFatal('admin_alert_acknowledge_failed')`.
 * A lista se atualiza sozinha pelo `onSnapshot` do `useAlerts`.
 *
 * Usado por: `AlertCard.tsx`, na tela `/dashboard/alerts` (`AlertsPage`).
 *
 * Arquitetura: `match /aggregations/alerts/items/{alertId}` de
 * `firebase/firestore.rules` só deixa o super-admin mexer em
 * `acknowledgedBy`/`acknowledgedAt`; o `acknowledgedAt` é carimbado no
 * servidor pelo trigger `onAlertWritten` (Sprint 22, PR E.6) — tempo do
 * servidor, nunca o relógio deste navegador. O `Date.now()` daqui só mede a
 * idade do alerta para a telemetria.
 *
 * Dicas: não gravar `acknowledgedAt` daqui; o campo `reason` do
 * `AlertSchema` é exclusivo de backfill por script (as rules o recusam).
 */
export function AcknowledgeButton({
  alertId,
  alertKind,
  observedAtMs,
  acknowledgedAt,
}: AcknowledgeButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const { status } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const acknowledged = acknowledgedAt !== null;

  async function handleClick(): Promise<void> {
    if (status.kind !== 'AUTHORIZED') return;
    setBusy(true);
    setError(null);
    try {
      assertCanWrite();
      // Sprint 22 PR E.6: cliente envia apenas `acknowledgedBy`; o trigger
      // `onAlertWritten` carimba `acknowledgedAt` server-side.
      const now = serverNow();
      await updateDoc(doc(firestore, 'aggregations', 'alerts', 'items', alertId), {
        acknowledgedBy: status.user.uid,
      });
      const ageDays = Math.max(0, Math.floor((now - observedAtMs) / D));
      trackEvents.biAlertAcknowledged(alertKind, ageDays);
    } catch (err) {
      reportNonFatal('admin_alert_acknowledge_failed', {
        alertId,
        alertKind,
        ...serializeError(err),
      });
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (acknowledged) {
    return (
      <Box fontSize="body-s" color="text-status-inactive">
        {t('alerts.ackedAt')}
      </Box>
    );
  }

  return (
    <SpaceBetween size="xxs">
      <Button
        loading={busy}
        onClick={(): void => {
          void handleClick();
        }}
      >
        {t('alerts.markSeen')}
      </Button>
      {error && (
        <Box fontSize="body-s" color="text-status-error">
          {error}
        </Box>
      )}
    </SpaceBetween>
  );
}
